import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, X, Trash2, Wand2, ImageIcon, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  MAX_OPTION_GROUPS,
  MAX_OPTION_VALUES_PER_GROUP,
  MAX_ACTIVE_COMBINATIONS,
  MAX_GROUP_NAME_LEN,
  MAX_OPTION_VALUE_LEN,
  cartesian,
  comboLabel,
  estimateCombinationCount,
  reconcileVariants,
  validateOptionGroups,
  validateVariantRow,
} from "./variantHelpers";

// Ukuran kontrol (scoped ke editor varian — tidak mengubah components/ui global)
const FIELD = "h-12 px-4 text-[15px] rounded-xl";
const GRID_FIELD = "h-11 px-3 text-sm rounded-lg";
const NARROW_QUERY = "(max-width: 767px)";

/** true bila viewport < md. Aman di jsdom (tanpa matchMedia → false). */
function useIsNarrow() {
  const [narrow, setNarrow] = useState(() => (
    typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(NARROW_QUERY).matches : false
  ));
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(NARROW_QUERY);
    const onChange = (e) => setNarrow(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange); else mq.addListener(onChange);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", onChange); else mq.removeListener(onChange); };
  }, []);
  return narrow;
}

function OptionGroupEditor({ groups, onChange, disabled }) {
  const addGroup = () => {
    if (groups.length >= MAX_OPTION_GROUPS) return;
    onChange([...groups, { name: "", values: [] }]);
  };
  const removeGroup = (idx) => {
    const next = groups.filter((_, i) => i !== idx);
    onChange(next);
  };
  const updateGroup = (idx, patch) => {
    const next = groups.map((g, i) => (i === idx ? { ...g, ...patch } : g));
    onChange(next);
  };

  return (
    <div className="space-y-4" data-testid="variant-option-groups">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm font-semibold text-[#1A1A1A]">Grup Opsi</div>
        <Button
          type="button"
          variant="outline"
          disabled={disabled || groups.length >= MAX_OPTION_GROUPS}
          onClick={addGroup}
          className="rounded-xl h-11 px-4"
          data-testid="add-option-group-btn"
        >
          <Plus size={16} className="mr-1.5" /> Tambah Grup ({groups.length}/{MAX_OPTION_GROUPS})
        </Button>
      </div>
      {groups.length === 0 && (
        <div className="text-sm text-[#5C5C5C] italic border border-dashed border-[#E5E5E5] rounded-xl p-4">
          Belum ada grup opsi. Contoh: <b>Warna</b> (Hitam, Putih) &middot; <b>Ukuran</b> (S, M, L).
        </div>
      )}
      {groups.map((g, idx) => (
        <OptionGroupRow
          key={idx}
          idx={idx}
          group={g}
          disabled={disabled}
          onChange={(patch) => updateGroup(idx, patch)}
          onRemove={() => removeGroup(idx)}
        />
      ))}
    </div>
  );
}

function OptionGroupRow({ idx, group, disabled, onChange, onRemove }) {
  const [pending, setPending] = useState("");
  const values = group.values || [];

  const addValue = () => {
    const v = pending.trim();
    if (!v) return;
    if (v.length > MAX_OPTION_VALUE_LEN) return;
    const exists = values.some((x) => x.toLowerCase() === v.toLowerCase());
    if (exists) { setPending(""); return; }
    if (values.length >= MAX_OPTION_VALUES_PER_GROUP) return;
    onChange({ values: [...values, v] });
    setPending("");
  };
  const removeValue = (i) => onChange({ values: values.filter((_, j) => j !== i) });

  return (
    <div className="border border-[#E5E5E5] rounded-xl p-4 space-y-3 bg-[#FAF9F6]/60" data-testid={`option-group-row-${idx}`}>
      <div>
        <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#5C5C5C]" htmlFor={`option-group-name-${idx}`}>Nama Grup</Label>
        <div className="flex items-center gap-2">
          <Input
            id={`option-group-name-${idx}`}
            value={group.name}
            onChange={(e) => onChange({ name: e.target.value.slice(0, MAX_GROUP_NAME_LEN) })}
            placeholder="Nama grup (mis. Warna, Ukuran)"
            disabled={disabled}
            className={`${FIELD} bg-white`}
            data-testid={`option-group-name-${idx}`}
          />
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={onRemove}
            disabled={disabled}
            className="rounded-xl h-12 w-12 shrink-0 text-[#DC2F02] hover:bg-red-50 hover:text-[#DC2F02]"
            data-testid={`remove-option-group-${idx}`}
            aria-label="Hapus grup"
          >
            <Trash2 size={18} />
          </Button>
        </div>
      </div>
      <div>
        <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#5C5C5C]" htmlFor={`add-option-value-input-${idx}`}>Nilai Opsi</Label>
        <div className={`flex flex-wrap gap-2 ${values.length ? "mb-2" : ""}`} data-testid={`option-values-${idx}`}>
            {values.map((v, i) => (
              <span
                key={`${v}-${i}`}
                className="inline-flex items-center gap-1.5 bg-white border border-[#E5E5E5] rounded-full pl-3.5 pr-1.5 h-9 text-sm"
                data-testid={`option-value-chip-${idx}-${i}`}
              >
                {v}
                <button
                  type="button"
                  onClick={() => removeValue(i)}
                  disabled={disabled}
                  className="rounded-full hover:bg-red-50 p-1.5 text-[#DC2F02]"
                  aria-label={`Hapus nilai ${v}`}
                  data-testid={`remove-option-value-${idx}-${i}`}
                >
                  <X size={14} />
                </button>
              </span>
            ))}
        </div>
        <div className="flex items-center gap-2">
          <Input
            id={`add-option-value-input-${idx}`}
            value={pending}
            onChange={(e) => setPending(e.target.value.slice(0, MAX_OPTION_VALUE_LEN))}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addValue(); }
            }}
            placeholder={`Ketik nilai lalu Enter (${values.length}/${MAX_OPTION_VALUES_PER_GROUP})`}
            disabled={disabled || values.length >= MAX_OPTION_VALUES_PER_GROUP}
            className={`${FIELD} bg-white`}
            data-testid={`add-option-value-input-${idx}`}
          />
          <Button
            type="button"
            variant="outline"
            onClick={addValue}
            disabled={disabled || !pending.trim() || values.length >= MAX_OPTION_VALUES_PER_GROUP}
            className="rounded-xl h-12 w-12 shrink-0 p-0 bg-white"
            data-testid={`add-option-value-btn-${idx}`}
            aria-label="Tambah nilai"
          >
            <Plus size={18} />
          </Button>
        </div>
      </div>
    </div>
  );
}

function BulkEditPanel({ selectedCount, onApply, disabled }) {
  const [field, setField] = useState("price");
  const [value, setValue] = useState("");
  const [skuPrefix, setSkuPrefix] = useState("");

  if (selectedCount === 0) return null;

  const apply = () => {
    if (field === "sku_prefix") {
      if (!skuPrefix.trim()) return;
      onApply({ field: "sku_prefix", value: skuPrefix.trim() });
      setSkuPrefix("");
    } else {
      if (value === "") return;
      onApply({ field, value });
      setValue("");
    }
  };

  return (
    <div className="bg-[#FAF9F6] border border-[#E5E5E5] rounded-xl p-4 flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3" data-testid="bulk-edit-panel">
      <div className="text-xs font-bold uppercase tracking-widest text-[#5C5C5C] sm:self-center sm:mr-1">
        {selectedCount} varian terpilih
      </div>
      <select
        value={field}
        onChange={(e) => setField(e.target.value)}
        disabled={disabled}
        className={`${FIELD} w-full sm:w-48 border border-[#E5E5E5] bg-white`}
        data-testid="bulk-edit-field"
        aria-label="Field bulk edit"
      >
        <option value="price">Harga</option>
        <option value="sale_price">Harga Diskon</option>
        <option value="cost_price">Harga Modal</option>
        <option value="stock">Stok</option>
        <option value="weight_grams">Berat (gr)</option>
        <option value="is_active_on">Aktifkan</option>
        <option value="is_active_off">Nonaktifkan</option>
        <option value="sku_prefix">SKU Prefix</option>
      </select>
      {field === "sku_prefix" ? (
        <Input
          value={skuPrefix}
          onChange={(e) => setSkuPrefix(e.target.value)}
          placeholder="mis. FSH-"
          className={`${FIELD} w-full sm:w-48 bg-white`}
          data-testid="bulk-edit-sku-prefix-input"
        />
      ) : field === "is_active_on" || field === "is_active_off" ? (
        <span className="text-sm text-[#5C5C5C] sm:self-center">Klik terapkan untuk mengubah status</span>
      ) : (
        <Input
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Nilai"
          className={`${FIELD} w-full sm:w-40 bg-white`}
          data-testid="bulk-edit-value-input"
        />
      )}
      <Button
        type="button"
        onClick={apply}
        disabled={disabled}
        className="brand-btn-primary rounded-xl h-12 px-5 w-full sm:w-auto"
        data-testid="bulk-edit-apply-btn"
      >
        Terapkan
      </Button>
    </div>
  );
}

/* Field editable per varian — satu sumber untuk layout tabel (desktop) dan kartu (mobile). */
function variantFields({ row, i, errors, updateRow, disabled, images }) {
  const err = (k) => (errors[k] ? "border-red-500 focus-visible:ring-red-500" : "");
  return [
    { key: "sku", label: "SKU", node: (
      <Input value={row.sku || ""} onChange={(e) => updateRow(i, { sku: e.target.value })} placeholder="-" disabled={disabled}
        className={`${GRID_FIELD} ${err("sku")}`} data-testid={`variant-sku-${i}`} />
    ) },
    { key: "price", label: "Harga", node: (
      <Input type="number" value={row.price ?? 0} onChange={(e) => updateRow(i, { price: e.target.value })} disabled={disabled}
        className={`${GRID_FIELD} ${err("price")}`} data-testid={`variant-price-${i}`} />
    ) },
    { key: "sale_price", label: "Diskon", node: (
      <Input type="number" value={row.sale_price ?? ""} onChange={(e) => updateRow(i, { sale_price: e.target.value === "" ? null : e.target.value })}
        placeholder="-" disabled={disabled} className={`${GRID_FIELD} ${err("sale_price")}`} data-testid={`variant-sale-${i}`} />
    ) },
    { key: "cost_price", label: "Modal", node: (
      <Input type="number" value={row.cost_price ?? 0} onChange={(e) => updateRow(i, { cost_price: e.target.value })} disabled={disabled}
        className={GRID_FIELD} data-testid={`variant-cost-${i}`} />
    ) },
    { key: "stock", label: "Stok", node: (
      <Input type="number" value={row.stock ?? 0} onChange={(e) => updateRow(i, { stock: e.target.value })} disabled={disabled}
        className={`${GRID_FIELD} ${err("stock")}`} data-testid={`variant-stock-${i}`} />
    ) },
    { key: "weight_grams", label: "Berat (gr)", node: (
      <Input type="number" value={row.weight_grams ?? 0} onChange={(e) => updateRow(i, { weight_grams: e.target.value })} disabled={disabled}
        className={`${GRID_FIELD} ${err("weight_grams")}`} data-testid={`variant-weight-${i}`} />
    ) },
    { key: "image_index", label: "Gambar", node: (
      <select
        value={row.image_index ?? ""}
        onChange={(e) => updateRow(i, { image_index: e.target.value === "" ? null : Number(e.target.value) })}
        disabled={disabled || (images || []).length === 0}
        className={`w-full ${GRID_FIELD} border bg-white disabled:opacity-50 ${errors.image_index ? "border-red-500" : "border-[#E5E5E5]"}`}
        data-testid={`variant-image-${i}`}
        aria-label="Gambar varian"
      >
        <option value="">-</option>
        {(images || []).map((_, idx) => (
          <option key={idx} value={idx}>#{idx + 1}</option>
        ))}
      </select>
    ) },
  ];
}

const COLUMN_WIDTHS = {
  sku: "min-w-[150px]", price: "min-w-[120px]", sale_price: "min-w-[120px]", cost_price: "min-w-[120px]",
  stock: "min-w-[100px]", weight_grams: "min-w-[100px]", image_index: "min-w-[100px]",
};

function VariantTable({ rows, selected, toggleAll, toggleOne, updateRow, removeRow, images, disabled }) {
  return (
    <div className="overflow-x-auto border border-[#E5E5E5] rounded-xl" data-testid="variant-grid-table">
      <table className="w-full text-sm min-w-[1080px]">
        <thead className="bg-[#FAF9F6] text-[11px] uppercase tracking-widest text-[#5C5C5C]">
          <tr>
            <th className="px-3 py-3 sticky left-0 bg-[#FAF9F6] z-10 w-12">
              <Checkbox
                checked={rows.length > 0 && selected.size === rows.length}
                onCheckedChange={toggleAll}
                disabled={disabled}
                className="h-5 w-5"
                data-testid="variant-select-all"
              />
            </th>
            <th className="px-3 py-3 text-left sticky left-12 bg-[#FAF9F6] z-10 min-w-[180px]">Kombinasi</th>
            <th className={`px-3 py-3 text-left ${COLUMN_WIDTHS.sku}`}>SKU</th>
            <th className={`px-3 py-3 text-left ${COLUMN_WIDTHS.price}`}>Harga</th>
            <th className={`px-3 py-3 text-left ${COLUMN_WIDTHS.sale_price}`}>Diskon</th>
            <th className={`px-3 py-3 text-left ${COLUMN_WIDTHS.cost_price}`}>Modal</th>
            <th className={`px-3 py-3 text-left ${COLUMN_WIDTHS.stock}`}>Stok</th>
            <th className={`px-3 py-3 text-left ${COLUMN_WIDTHS.weight_grams}`}>Berat</th>
            <th className={`px-3 py-3 text-left ${COLUMN_WIDTHS.image_index}`}>Gambar</th>
            <th className="px-3 py-3 text-center w-20">Aktif</th>
            <th className="px-3 py-3 w-14"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const errors = validateVariantRow(row, { images });
            const fields = variantFields({ row, i, errors, updateRow, disabled, images });
            return (
              <tr
                key={row._key || i}
                className={`border-t border-[#E5E5E5] ${row._matched === false ? "bg-emerald-50/30" : ""}`}
                data-testid={`variant-row-${i}`}
              >
                <td className="px-3 py-2.5 sticky left-0 bg-white z-10 align-middle">
                  <Checkbox checked={selected.has(i)} onCheckedChange={() => toggleOne(i)} className="h-5 w-5" data-testid={`variant-select-${i}`} />
                </td>
                <td className="px-3 py-2.5 sticky left-12 bg-white z-10 font-medium align-middle">
                  <div className="line-clamp-2" title={comboLabel(row.option_values)}>{comboLabel(row.option_values)}</div>
                  {!row._matched && row.id === null && (
                    <span className="inline-block mt-0.5 text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Baru</span>
                  )}
                </td>
                {fields.map((f) => (
                  <td key={f.key} className={`px-3 py-2.5 align-middle ${COLUMN_WIDTHS[f.key]}`}>{f.node}</td>
                ))}
                <td className="px-3 py-2.5 text-center align-middle">
                  <Switch checked={!!row.is_active} onCheckedChange={(v) => updateRow(i, { is_active: v })} disabled={disabled} data-testid={`variant-active-${i}`} />
                </td>
                <td className="px-3 py-2.5 align-middle">
                  <button
                    type="button"
                    onClick={() => removeRow(i)}
                    disabled={disabled}
                    className="text-[#DC2F02] hover:bg-red-50 rounded-lg h-11 w-11 grid place-items-center"
                    aria-label="Hapus baris"
                    data-testid={`variant-remove-${i}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function VariantCards({ rows, selected, toggleAll, toggleOne, updateRow, removeRow, images, disabled }) {
  return (
    <div className="space-y-3" data-testid="variant-grid-cards">
      <label className="flex items-center gap-3 min-h-[44px] px-3 rounded-xl border border-[#E5E5E5] bg-[#FAF9F6] text-sm font-medium">
        <Checkbox
          checked={rows.length > 0 && selected.size === rows.length}
          onCheckedChange={toggleAll}
          disabled={disabled}
          className="h-5 w-5"
          data-testid="variant-select-all"
        />
        Pilih semua varian
      </label>
      {rows.map((row, i) => {
        const errors = validateVariantRow(row, { images });
        const fields = variantFields({ row, i, errors, updateRow, disabled, images });
        return (
          <div
            key={row._key || i}
            className={`border border-[#E5E5E5] rounded-xl p-4 space-y-3 ${row._matched === false ? "bg-emerald-50/30" : "bg-white"}`}
            data-testid={`variant-row-${i}`}
          >
            <div className="flex items-start gap-3">
              <Checkbox checked={selected.has(i)} onCheckedChange={() => toggleOne(i)} className="h-5 w-5 mt-0.5" data-testid={`variant-select-${i}`} />
              <div className="flex-1 min-w-0 font-medium text-sm">
                <div className="break-words">{comboLabel(row.option_values)}</div>
                {!row._matched && row.id === null && (
                  <span className="inline-block mt-0.5 text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Baru</span>
                )}
              </div>
              <label className="flex items-center gap-2 text-xs text-[#5C5C5C] shrink-0">
                Aktif
                <Switch checked={!!row.is_active} onCheckedChange={(v) => updateRow(i, { is_active: v })} disabled={disabled} data-testid={`variant-active-${i}`} />
              </label>
              <button
                type="button"
                onClick={() => removeRow(i)}
                disabled={disabled}
                className="text-[#DC2F02] hover:bg-red-50 rounded-lg h-11 w-11 grid place-items-center shrink-0 -mr-2 -mt-2"
                aria-label="Hapus baris"
                data-testid={`variant-remove-${i}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {fields.map((f) => (
                <div key={f.key} className={f.key === "sku" ? "col-span-2" : ""}>
                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-[#5C5C5C]">{f.label}</div>
                  {f.node}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function VariantGrid({ rows, onChange, images, disabled }) {
  const [selected, setSelected] = useState(new Set());
  const narrow = useIsNarrow();

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((_, i) => i)));
  };
  const toggleOne = (i) => {
    const next = new Set(selected);
    if (next.has(i)) next.delete(i); else next.add(i);
    setSelected(next);
  };

  const updateRow = (i, patch) => {
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const removeRow = (i) => {
    const next = rows.filter((_, idx) => idx !== i);
    onChange(next);
    const s = new Set(selected); s.delete(i); setSelected(s);
  };

  const applyBulk = ({ field, value }) => {
    const idxs = Array.from(selected);
    let counter = 0;
    const next = rows.map((r, i) => {
      if (!selected.has(i)) return r;
      counter += 1;
      if (field === "sku_prefix") {
        const suffix = (r.option_values || []).map((ov) => ov.value.replace(/\s+/g, "").toUpperCase()).join("-");
        return { ...r, sku: `${value}${suffix}` };
      }
      if (field === "is_active_on") return { ...r, is_active: true };
      if (field === "is_active_off") return { ...r, is_active: false };
      if (field === "sale_price") return { ...r, sale_price: value === "" || value === null ? null : Number(value) };
      return { ...r, [field]: Number(value) };
    });
    onChange(next);
    return idxs.length;
  };

  if (!rows.length) {
    return (
      <div className="text-sm text-[#5C5C5C] italic border border-dashed border-[#E5E5E5] rounded-xl p-4" data-testid="variant-grid-empty">
        Belum ada kombinasi varian. Klik <b>Generate Kombinasi</b> setelah opsi lengkap.
      </div>
    );
  }

  const layoutProps = { rows, selected, toggleAll, toggleOne, updateRow, removeRow, images, disabled };
  return (
    <div className="space-y-3" data-testid="variant-grid" data-layout={narrow ? "cards" : "table"}>
      <BulkEditPanel selectedCount={selected.size} onApply={applyBulk} disabled={disabled} />
      {narrow ? <VariantCards {...layoutProps} /> : <VariantTable {...layoutProps} />}
    </div>
  );
}

/**
 * VariantManager — top-level Sprint 2 UX for a single product form.
 *
 * Props:
 *   optionGroups           : current option_groups (form state, controlled)
 *   onOptionGroupsChange   : setter
 *   variants               : current variant rows (controlled)
 *   onVariantsChange       : setter
 *   images                 : product.images (for image_index dropdown)
 *   defaults               : { price, cost_price, stock, weight_grams } used to seed new rows
 *   disabled               : boolean
 */
export default function VariantManager({
  optionGroups,
  onOptionGroupsChange,
  variants,
  onVariantsChange,
  images,
  defaults,
  disabled,
}) {
  const groupErrors = useMemo(() => validateOptionGroups(optionGroups), [optionGroups]);
  const estimatedCount = useMemo(() => estimateCombinationCount(optionGroups), [optionGroups]);
  const overCap = estimatedCount > MAX_ACTIVE_COMBINATIONS;
  const canGenerate = groupErrors.length === 0 && estimatedCount > 0 && !overCap && !disabled;

  const generate = useCallback(() => {
    const combos = cartesian(optionGroups);
    if (!combos.length) return;
    if (combos.length > MAX_ACTIVE_COMBINATIONS) return;
    const { rows } = reconcileVariants(combos, variants, defaults || {});
    onVariantsChange(rows);
  }, [optionGroups, variants, defaults, onVariantsChange]);

  return (
    <div className="space-y-5 p-4 sm:p-5 border border-[#E5E5E5] rounded-2xl bg-white" data-testid="variant-manager">
      <OptionGroupEditor groups={optionGroups} onChange={onOptionGroupsChange} disabled={disabled} />

      {groupErrors.length > 0 && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3 space-y-0.5" data-testid="option-groups-errors">
          {groupErrors.map((e, i) => (<div key={i}>• {e}</div>))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 border-t border-[#E5E5E5] pt-4">
        <Button
          type="button"
          onClick={generate}
          disabled={!canGenerate}
          className="brand-btn-primary rounded-xl h-11 px-5 w-full sm:w-auto"
          data-testid="generate-combinations-btn"
        >
          <Wand2 size={16} className="mr-1.5" /> Generate Kombinasi
        </Button>
        <div className="text-sm" data-testid="combination-count">
          Estimasi:{" "}
          <b className={overCap ? "text-red-700" : "text-[#1A1A1A]"}>{estimatedCount}</b>
          <span className="text-[#5C5C5C]"> / {MAX_ACTIVE_COMBINATIONS} kombinasi</span>
        </div>
        {overCap && (
          <div className="text-sm text-red-700 flex items-center gap-1.5" data-testid="combination-over-cap">
            <AlertTriangle size={16} /> Melebihi batas — kurangi nilai opsi.
          </div>
        )}
      </div>

      <div className="border-t border-[#E5E5E5] pt-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <Label className="text-sm font-semibold">Daftar Varian ({variants.length})</Label>
          {(!images || images.length === 0) && variants.length > 0 && (
            <div className="text-xs text-[#5C5C5C] italic flex items-center gap-1.5">
              <ImageIcon size={14} /> Upload gambar produk untuk memilih per varian.
            </div>
          )}
        </div>
        <VariantGrid rows={variants} onChange={onVariantsChange} images={images} disabled={disabled} />
      </div>
    </div>
  );
}
